import {
  TCTemplateKind,
  UserKind,
  type FunnelStatus,
  type TCTemplateRecord,
  type User,
} from 'src/api/generated';
import type { MappingValue } from 'src/modules/automations/components/molecules/create-trigger-modal/mapping-row';
import type {
  SemanticQuickAutomationKind,
  SemanticQuickAutomationTemplateValidation,
} from 'src/modules/automations/types/semantic-quick-automation.type';
import type { LiteralType } from './template-definition.type';
import { getTemplateSchemaMetadata } from './template-mapping.util';
import { isRecord, isTemplateDefinition } from './template-definition.util';

/**
 * Stable template keys with exact semantic adapters in Quick automation.
 */
export const SEMANTIC_QUICK_AUTOMATION_TEMPLATE_KEYS = {
  ArchiveConversation: 'archive-conversation',
  AssignSpecificUserOnStatus: 'assign-specific-user-on-status',
  AssignSpecificUserOnCreatedStatus: 'assign-specific-user-on-created-status',
} as const;

const TRIGGER_IDS = {
  ConversationStatusUpdated: 'llamadapro.trigger.conversation_status_updated',
  ConversationCreated: 'llamadapro.trigger.conversation_created',
} as const;

interface SemanticTemplateContract {
  /** Semantic controls rendered for the template. */
  kind: SemanticQuickAutomationKind;
  /** Exact trigger required by the workflow definition. */
  triggerId: string;
  /** Exact workflow input fields and their expected literal types. */
  inputTypes: Record<string, LiteralType>;
  /** Trigger event paths that must remain locked by the adapter. */
  eventMappings: Record<string, string>;
}

const STATUS_UPDATED_EVENT_MAPPINGS = {
  conversationId: 'data.id',
  conversationFunnelStatusId: 'data.funnelStatusId',
} as const;

const ASSIGNMENT_INPUT_TYPES = {
  funnelStatusId: 'number',
  userId: 'number',
  conversationId: 'number',
  conversationFunnelStatusId: 'number',
} as const;

const TEMPLATE_CONTRACTS: Record<string, SemanticTemplateContract> = {
  [SEMANTIC_QUICK_AUTOMATION_TEMPLATE_KEYS.ArchiveConversation]: {
    kind: 'status-only',
    triggerId: TRIGGER_IDS.ConversationStatusUpdated,
    inputTypes: {
      funnelStatusId: 'number',
      conversationId: 'number',
      updatedAt: 'string',
      conversationFunnelStatusId: 'number',
    },
    eventMappings: {
      conversationId: 'resourceId',
      updatedAt: 'data.updatedAt',
      conversationFunnelStatusId: 'data.funnelStatusId',
    },
  },
  [SEMANTIC_QUICK_AUTOMATION_TEMPLATE_KEYS.AssignSpecificUserOnStatus]: {
    kind: 'status-and-person-user',
    triggerId: TRIGGER_IDS.ConversationStatusUpdated,
    inputTypes: ASSIGNMENT_INPUT_TYPES,
    eventMappings: STATUS_UPDATED_EVENT_MAPPINGS,
  },
  [SEMANTIC_QUICK_AUTOMATION_TEMPLATE_KEYS.AssignSpecificUserOnCreatedStatus]: {
    kind: 'status-and-person-user',
    triggerId: TRIGGER_IDS.ConversationCreated,
    inputTypes: ASSIGNMENT_INPUT_TYPES,
    eventMappings: STATUS_UPDATED_EVENT_MAPPINGS,
  },
};

const resolveContract = (
  templateKey: string | undefined
): SemanticTemplateContract | null =>
  templateKey ? TEMPLATE_CONTRACTS[templateKey] ?? null : null;

const hasExactInputSchema = (
  template: TCTemplateRecord,
  contract: SemanticTemplateContract
): boolean => {
  const { fieldKeys, fieldExpectedLiteralTypes, requiredFields } =
    getTemplateSchemaMetadata(template);
  const expectedFields = Object.keys(contract.inputTypes);

  return (
    fieldKeys.length === expectedFields.length &&
    expectedFields.every(
      (field) =>
        fieldKeys.includes(field) &&
        requiredFields.has(field) &&
        fieldExpectedLiteralTypes[field] === contract.inputTypes[field]
    ) &&
    requiredFields.size === expectedFields.length
  );
};

const hasObjectInputSchema = (template: TCTemplateRecord): boolean =>
  isRecord(template.definition) &&
  isRecord(template.definition.inputSchema) &&
  template.definition.inputSchema.type === 'object';

const hasExactEventMappings = (
  template: TCTemplateRecord,
  contract: SemanticTemplateContract
): boolean => {
  const instructions = template.trigger?.inputMapping.instructions;
  const expectedMappings = Object.entries(contract.eventMappings);
  if (!instructions || instructions.length !== expectedMappings.length) {
    return false;
  }

  return expectedMappings.every(
    ([field, expectedPath]) =>
      instructions.filter(
        (instruction) =>
          instruction.workflowInputField === field &&
          instruction.triggerDataPath === expectedPath
      ).length === 1
  );
};

/**
 * Determines whether a template summary has a known semantic adapter.
 *
 * @param templateKey - Stable Templates App key.
 * @returns True only for keys whose production contract is represented here.
 */
export const isSemanticQuickAutomationTemplateKey = (
  templateKey: string | undefined
): boolean => resolveContract(templateKey) !== null;

/**
 * Validates a known template before hiding its raw trigger mapping.
 *
 * Recognized keys fail closed if their kind, trigger, complete required input
 * schema, or event mappings drift. Mapping-version differences are accepted
 * because they do not change the workflow's semantic inputs. Unknown keys
 * return `kind: null` so the generic editor remains available.
 *
 * @param template - Full template detail returned by Templates App.
 * @returns Adapter kind and whether semantic configuration is safe.
 */
export const validateSemanticQuickAutomationTemplate = (
  template: TCTemplateRecord | null
): SemanticQuickAutomationTemplateValidation => {
  const contract = resolveContract(template?.key);
  if (!contract) {
    return { kind: null, valid: false };
  }

  if (
    !template ||
    template.kind !== TCTemplateKind.WORKFLOW ||
    template.trigger?.id !== contract.triggerId ||
    !isTemplateDefinition(template.definition) ||
    !hasObjectInputSchema(template) ||
    !hasExactInputSchema(template, contract) ||
    !hasExactEventMappings(template, contract)
  ) {
    return { kind: contract.kind, valid: false };
  }

  return { kind: contract.kind, valid: true };
};

const isSelectableId = (value: number): boolean =>
  Number.isSafeInteger(value) && value >= 0;

const readNumberLiteral = (
  mapping: Record<string, MappingValue>,
  field: string
): number | null => {
  if (!Object.prototype.hasOwnProperty.call(mapping, field)) {
    return null;
  }
  const value = mapping[field];
  if (value.kind !== 'literal' || value.literalType !== 'number') {
    return null;
  }
  const parsed = Number(value.value);
  return isSelectableId(parsed) ? parsed : null;
};

/**
 * Verifies that semantic ID literals still resolve to selectable tenant data.
 *
 * @param mapping - Current trigger mapping controlled by the semantic editor.
 * @param statuses - Statuses from the current Communications funnel.
 * @param users - Person Users returned for the current tenant.
 * @param kind - Exact semantic control set required by the template.
 * @returns True when every required ID resolves to the supplied tenant data.
 */
export const isSemanticQuickAutomationMappingValid = (
  mapping: Record<string, MappingValue>,
  statuses: FunnelStatus[],
  users: User[],
  kind: SemanticQuickAutomationKind
): boolean => {
  const statusId = readNumberLiteral(mapping, 'funnelStatusId');
  if (statusId === null || !statuses.some((status) => status.id === statusId)) {
    return false;
  }

  if (kind === 'status-only') {
    return true;
  }

  const userId = readNumberLiteral(mapping, 'userId');
  return (
    userId !== null &&
    users.some((user) => user.id === userId && user.kind === UserKind.PERSON)
  );
};

/**
 * Adds semantic selections to an existing trigger mapping.
 *
 * Existing event mappings and unrelated literals remain unchanged. A `null`
 * selection leaves that field untouched, allowing the current-column preset
 * to survive before the operator makes an explicit selection.
 *
 * @param baseMapping - Existing mapping decoded from the template and presets.
 * @param selectedStatusId - Selected funnel-status ID, or null to preserve it.
 * @param selectedUserId - Selected person-User ID, or null to preserve it.
 * @returns A new mapping with selected IDs represented as number literals.
 * @throws {RangeError} When a provided ID is not a non-negative safe integer.
 */
export const buildSemanticQuickAutomationMapping = (
  baseMapping: Record<string, MappingValue>,
  selectedStatusId: number | null,
  selectedUserId: number | null
): Record<string, MappingValue> => {
  if (
    (selectedStatusId !== null && !isSelectableId(selectedStatusId)) ||
    (selectedUserId !== null && !isSelectableId(selectedUserId))
  ) {
    throw new RangeError(
      'Semantic quick-automation IDs must be non-negative safe integers.'
    );
  }

  return {
    ...baseMapping,
    ...(selectedStatusId === null
      ? {}
      : {
          funnelStatusId: {
            kind: 'literal' as const,
            literalType: 'number' as const,
            value: String(selectedStatusId),
          },
        }),
    ...(selectedUserId === null
      ? {}
      : {
          userId: {
            kind: 'literal' as const,
            literalType: 'number' as const,
            value: String(selectedUserId),
          },
        }),
  };
};
